import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { keys } from './queries';
import type { Project } from './types';

// Goal-first project setup: docs/plans/2026-09-11-goal-first-onboarding.md
// (875fed0), as implemented by packages/core/src/project-setup.ts and
// apps/api/src/projects.ts (9c6e91d).

export type SourceService = 'github' | 'discord' | 'notion' | 'other';

/** An intended input. A saved reference is not an authenticated connection. */
export interface SetupSource {
  id: string;
  service: SourceService;
  label: string;
  reference?: string;
  purpose: string;
}

/** An intended project/topic thread. Not a grant to read or post. */
export interface SetupDestination {
  id: string;
  title: string;
  purpose: string;
  expectedContent: string;
  reference?: string;
}

export interface ProjectSetup {
  goal: string;
  /** The owner's description of useful progress; not a verified result. */
  successSignal?: string;
  sources: SetupSource[];
  destinations: SetupDestination[];
}

/** Connection state is the service's to report; today every saved source is `not_connected`. */
export interface SourceConnection {
  sourceId: string;
  service: SourceService;
  status: string;
}

/**
 * GET/PUT /api/projects/:id/setup. Only the project's manager (or the
 * authorized service owner for an unclaimed pilot) can read it; anyone else
 * gets 404. An unconfigured pilot has revision 0 and a null setup.
 */
export interface SetupEnvelope {
  projectId: string;
  revision: number;
  canEdit: boolean;
  setup: ProjectSetup | null;
  connections: SourceConnection[];
}

export type Audience = 'private' | 'members';

export interface CreateProjectInput {
  title: string;
  summary?: string;
  audience: Audience;
  setup: ProjectSetup;
  /** One key per creation attempt ([A-Za-z0-9_-]{8,200}); an exact resend reuses it and gets the original project back. */
  idempotencyKey: string;
}

/** 201 when created; 200 when an exact resend replays the original creation. */
export interface CreateProjectResult extends SetupEnvelope {
  project: Project;
}

const enc = encodeURIComponent;
export const setupKey = (id: string) => ['project-setup', id] as const;

export const useProjectSetup = (id: string, enabled = true) =>
  useQuery({
    queryKey: setupKey(id),
    queryFn: ({ signal }) => api.get<SetupEnvelope>(`/api/projects/${enc(id)}/setup`, signal),
    enabled: enabled && id !== '',
  });

/** Saves a complete setup against the revision it was edited from. Never retried automatically. */
export function useSaveSetup(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { expectedRevision: number; setup: ProjectSetup }) => api.put<SetupEnvelope>(`/api/projects/${enc(id)}/setup`, input),
    retry: false,
    onSuccess: (envelope) => client.setQueryData(setupKey(id), envelope),
  });
}

/** Creates a managed project; the service assigns its ID and manager. Never retried automatically. */
export function useCreateProject() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) => api.post<CreateProjectResult>('/api/projects', input),
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.projects }),
  });
}
